import React, { useState, useRef } from "react";
import { FaUpload, FaFileAlt, FaTimes } from "react-icons/fa";

export default function KnowledgeUploadForm({ deviceUrl, onUploadSuccess }) {
  const [file, setFile] = useState(null);
  const [subject, setSubject] = useState("");
  const [level, setLevel] = useState("");
  const [purpose, setPurpose] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const ACCEPTED_TYPES = ".pdf,.docx,.txt,.md,.markdown";

  const handleUpload = async () => {
    if (!file) {
      setStatus("Please select a file first");
      return;
    }

    setLoading(true);
    setStatus("Uploading to local device...");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("subject", subject);
    formData.append("level", level);
    formData.append("purpose", purpose);

    try {
      const res = await fetch(`${deviceUrl}/api/documents/upload`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Upload failed");
      }

      setStatus(
        `Success!\n\nDocument ID: ${data.document_id}\n` +
          `Original name: ${data.original_name}\n` +
          `Text length: ${data.text_length} characters`
      );

      if (onUploadSuccess) {
        onUploadSuccess();
      }

      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setStatus(
        `Error: ${err.message}\n\nMake sure the local device is running and the address is correct.`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFileChosen = (chosenFile) => {
    if (!chosenFile) return;
    setFile(chosenFile);
    setStatus("");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFileChosen(dropped);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      {/* ===== File picker ===== */}
      <div style={{ marginBottom: "16px" }}>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          onChange={(e) => handleFileChosen(e.target.files?.[0] || null)}
          style={{ display: "none" }}
        />

        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            padding: "26px 20px",
            background: isDragOver ? "rgba(2,139,232,0.12)" : "#1a1a2e",
            border: `1.5px dashed ${isDragOver ? "#028be8" : "#444"}`,
            borderRadius: "10px",
            cursor: "pointer",
            transition: "all 0.18s ease",
          }}
        >
          {!file ? (
            <>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 20px",
                  background: "rgba(2,139,232,0.15)",
                  border: "1px solid #028be8",
                  borderRadius: "8px",
                  color: "#36a7f8",
                  fontWeight: 600,
                  fontSize: "0.92rem",
                }}
              >
                <FaUpload />
                Choose File
              </div>
              <p style={{ margin: 0, fontSize: "0.8rem", color: "#777" }}>
                or drag and drop &nbsp;·&nbsp; PDF, DOCX, TXT, MD
              </p>
            </>
          ) : (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                width: "100%",
                padding: "10px 14px",
                background: "rgba(54,167,248,0.08)",
                border: "1px solid rgba(54,167,248,0.3)",
                borderRadius: "8px",
              }}
            >
              <FaFileAlt style={{ color: "#36a7f8", fontSize: "1.3rem", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    color: "#e0f7ff",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {file.name}
                </div>
                <div style={{ color: "#888", fontSize: "0.78rem", marginTop: "2px" }}>
                  {formatFileSize(file.size)}
                </div>
              </div>
              <button
                onClick={() => {
                  setFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                title="Remove file"
                style={{
                  background: "none",
                  border: "none",
                  color: "#888",
                  cursor: "pointer",
                  fontSize: "1rem",
                  padding: "6px",
                  flexShrink: 0,
                }}
              >
                <FaTimes />
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "block", fontWeight: "200", fontSize: "16px", marginBottom: "4px", color: "#b0b0b0" }}>
          Subject
        </label>
        <input
          value={subject}
          placeholder="e.g., Mathematics, History, Biology"
          onChange={(e) => setSubject(e.target.value)}
          style={{ width: "100%", padding: "8px 10px", background: "#1a1a2e", border: "1px solid #444", borderRadius: "6px", color: "white" }}
        />
      </div>

      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "block", fontWeight: "200", fontSize: "16px", marginBottom: "4px", color: "#b0b0b0" }}>
          Level
        </label>
        <input
          value={level}
          placeholder="e.g., High School, University, Professional"
          onChange={(e) => setLevel(e.target.value)}
          style={{ width: "100%", padding: "8px 10px", background: "#1a1a2e", border: "1px solid #444", borderRadius: "6px", color: "white" }}
        />
      </div>

      <div style={{ marginBottom: "18px" }}>
        <label style={{ display: "block", fontWeight: "200", fontSize: "16px", marginBottom: "4px", color: "#b0b0b0" }}>
          Purpose
        </label>
        <input
          value={purpose}
          placeholder="e.g., Exam preparation, Research, Personal learning"
          onChange={(e) => setPurpose(e.target.value)}
          style={{ width: "100%", padding: "8px 10px", background: "#1a1a2e", border: "1px solid #444", borderRadius: "6px", color: "white" }}
        />
      </div>

      <button
        onClick={handleUpload}
        disabled={!file || loading}
        style={{
          width: "100%",
          padding: "12px",
          background: loading ? "#555" : !file ? "#333" : "#028be8",
          color: "white",
          border: "none",
          borderRadius: "8px",
          fontSize: "1rem",
          fontWeight: 600,
          cursor: loading || !file ? "not-allowed" : "pointer",
        }}
      >
        {loading ? "Processing on your AspireAI device..." : "Upload"}
      </button>

      {status && (
        <pre
          style={{
            marginTop: "16px",
            padding: "12px",
            background: "#1a1a2e",
            borderRadius: "8px",
            whiteSpace: "pre-wrap",
            fontSize: "0.9rem",
            color: status.startsWith("Success") ? "#4ade80" : "#f87171",
          }}
        >
          {status}
        </pre>
      )}
    </div>
  );
}